import { JSX, useEffect, useState } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';

import { Typography } from 'components/typography';
import { NormalizedLoad } from 'types/Load';
import { Undef } from 'types/UtilityTypes';

import { LoadBuildingTextInput } from '../components/LoadBuildingTextInput';
import { RateType } from '../components/RateGrid';
import { PlsLane, usePlsEstimatedMiles } from './hooks/usePlsEstimatedMiles';
import { PlsEstimatedMiles, PlsLaneMilesSource } from './types';

// Where an estimate came from, under the miles it filled.
const SOURCE_NOTES: Record<PlsLaneMilesSource, string> = {
  [PlsLaneMilesSource.History]:
    "Estimated from this customer's loads on the lane.",
  [PlsLaneMilesSource.MileageLookup]: 'Estimated from Quick Quote mileage.',
};

interface PlsTripMilesProps {
  formMethods: UseFormReturn<NormalizedLoad>;
  tmsID: number | undefined;
}

// The miles a per-mile rate rates on, in the rate grid's units field. PLS needs the line haul in
// dollars for a percent fuel line and only computes miles after create, so when the tender states
// none the card fills an estimate, says where it came from, and lets the rep correct it.
export function PlsTripMiles({
  formMethods,
  tmsID,
}: PlsTripMilesProps): JSX.Element {
  const { control, setValue } = formMethods;
  const [rateType, fscPercent, miles, customerID, stops] = useWatch({
    control,
    name: [
      'rateData.customerRateType',
      'rateData.fscPercent',
      'specifications.totalDistance.val',
      'customer.externalTMSID',
      'stops',
    ],
  });
  // The estimate the card filled and for which lane, so its note shows until the rep changes the
  // miles, and a rep who clears the field isn't refilled for the same lane.
  const [filled, setFilled] =
    useState<Undef<{ estimate: PlsEstimatedMiles; laneKey: string }>>();

  const origin = stops?.[0]?.address;
  const destination = stops?.[stops.length - 1]?.address;
  const needsMiles =
    rateType === RateType.Distance &&
    (fscPercent ?? 0) > 0 &&
    !((miles ?? 0) > 0) &&
    Boolean(origin?.city && destination?.city);
  const lane: Undef<PlsLane> = needsMiles
    ? {
        customerID: customerID ?? '',
        originZip: origin?.zip ?? '',
        originCity: origin?.city ?? '',
        originState: origin?.state ?? '',
        destinationZip: destination?.zip ?? '',
        destinationCity: destination?.city ?? '',
        destinationState: destination?.state ?? '',
      }
    : undefined;
  const laneKey = [
    customerID,
    origin?.zip,
    origin?.city,
    origin?.state,
    destination?.zip,
    destination?.city,
    destination?.state,
  ].join('|');
  const { estimate, isLoading } = usePlsEstimatedMiles(tmsID, lane);

  // Fill the estimate once per lane, when it arrives for an empty miles field. The rep's own miles
  // always win: clearing the field to retype doesn't bring the estimate back.
  const alreadyFilledLane = filled?.laneKey === laneKey;
  useEffect(() => {
    if (
      !needsMiles ||
      alreadyFilledLane ||
      !estimate ||
      !(estimate.miles > 0)
    ) {
      return;
    }
    setValue(
      'specifications.totalDistance',
      { val: estimate.miles, unit: null },
      { shouldDirty: true }
    );
    setFilled({ estimate, laneKey });
  }, [needsMiles, alreadyFilledLane, estimate, laneKey, setValue]);

  const filledEstimate = filled?.estimate;
  const showsEstimate =
    filledEstimate !== undefined && miles === filledEstimate.miles;

  return (
    <div className='col-span-2' data-testid='pls-trip-miles'>
      <LoadBuildingTextInput
        name='specifications.totalDistance.val'
        label='Miles'
        inputType='number'
        options={{ valueAsNumber: true, min: 0 }}
        required={false}
        placeholder={isLoading ? 'Estimating...' : 'Miles'}
      />
      {showsEstimate && filledEstimate.source && (
        <Typography variant='body-xs' className='text-neutral-500 mt-1'>
          {`${SOURCE_NOTES[filledEstimate.source]} PLS recomputes miles after create.`}
        </Typography>
      )}
    </div>
  );
}
